/**
 * Template-authored behaviors → scene behavior data.
 *
 * A template MAY author `properties.behaviors`. image-wheel does: its ring
 * carries an `AnimationSequence` spinning the tiles, and the converter dropped
 * it, so the one reference case that exists to exercise animation rendered
 * static. What it authors is already trace vocabulary — `@type`-discriminated,
 * matching `AnimationSequenceData` field for field — so carrying it through is
 * a filter, not a translation. A separate "motion layer" would be new machinery
 * to re-express data we can pass as-is.
 *
 * Two rules make the pass-through safe, and both are the point of the module
 * rather than incidental to it (CONTRACT.md §3.2):
 *
 * 1. **Allowlist, never wildcard.** A template is content; it must not be able
 *    to smuggle arbitrary scripted behavior into a scene by naming a `@type`.
 *    Extending the list is a deliberate act, one behavior at a time, and an
 *    unlisted `@type` stays a coverage gap rather than a silent pass.
 * 2. **Keys the schema names, and nothing else.** The artifact authors
 *    `wrapMode: "Loop"`, which is not in `AnimationStateConfig` — the main slot
 *    loops by definition — so it is dropped rather than riding along as a field
 *    nothing reads.
 */

import type { AnimationSequenceData, AnimationStateConfig } from 'trace-viewer/behaviors';

/** Where a template's own assets are served from; relative `src` resolves here. */
const TEMPLATE_ASSET_BASE = '/ai-templates/';

/** Behavior data a template is allowed to author, by `@type`. */
type TemplateBehaviorData = AnimationSequenceData;

const ANIMATION_STATES = ['enter', 'main', 'exit'] as const;
/** Numeric fields of `AnimationStateConfig`; `src` and `clip` are handled apart. */
const STATE_NUMBERS = ['speed', 'delay'] as const;

/**
 * A template's asset paths are relative to where its own assets live; scene
 * `src` values are site-absolute. An already-absolute path or a URL is left
 * alone — a template may legitimately reference hosted media.
 */
function resolveAssetSrc(src: string): string {
    if (src.startsWith('/') || /^[a-z]+:/i.test(src)) return src;
    return `${TEMPLATE_ASSET_BASE}${src}`;
}

function animationState(raw: unknown): AnimationStateConfig | null {
    if (!raw || typeof raw !== 'object') return null;
    const authored = raw as Record<string, unknown>;
    const out: AnimationStateConfig = {};
    if (typeof authored.src === 'string' && authored.src) out.src = resolveAssetSrc(authored.src);
    if (typeof authored.clip === 'string' && authored.clip) out.clip = authored.clip;
    for (const field of STATE_NUMBERS) {
        const v = authored[field];
        if (typeof v === 'number') out[field] = v;
    }
    return Object.keys(out).length ? out : null;
}

function animationSequence(raw: Record<string, unknown>): AnimationSequenceData | null {
    const states = raw.states;
    if (!states || typeof states !== 'object') return null;
    const out: AnimationSequenceData['states'] = {};
    for (const slot of ANIMATION_STATES) {
        const state = animationState((states as Record<string, unknown>)[slot]);
        if (state) out[slot] = state;
    }
    return Object.keys(out).length ? { '@type': 'AnimationSequence', states: out } : null;
}

/** `@type` → the filter that turns authored data into scene behavior data. */
const ALLOWED: Record<string, (raw: Record<string, unknown>) => TemplateBehaviorData | null> = {
    AnimationSequence: animationSequence,
};

/** Is this `@type` one a template may author? Mirrors the coverage harness. */
export function isAllowedTemplateBehavior(type: unknown): boolean {
    return typeof type === 'string' && type in ALLOWED;
}

/**
 * Filter a node's authored `behaviors` down to what a template may carry.
 * Anything unlisted, malformed, or empty after filtering is dropped — the
 * coverage harness is what reports it, so a new `@type` shows up as a gap
 * instead of disappearing.
 */
export function templateBehaviors(authored: unknown): TemplateBehaviorData[] {
    if (!Array.isArray(authored)) return [];
    const out: TemplateBehaviorData[] = [];
    for (const entry of authored) {
        if (!entry || typeof entry !== 'object') continue;
        const raw = entry as Record<string, unknown>;
        const filter = typeof raw['@type'] === 'string' ? ALLOWED[raw['@type']] : undefined;
        const data = filter?.(raw);
        if (data) out.push(data);
    }
    return out;
}
